import {
  Speedometer,
  PencilSimple,
  X,
  CheckCircle,
  Clock,
} from '@phosphor-icons/react';
import React from 'react';

import { Pagination, Select } from 'antd';
import { IOdometerHistory } from '../../OdometerHistory';
import { FieldErrors, UseFormRegister } from 'react-hook-form';
import { MileageReportRequestUpdate } from '../../SubmitMileage';
import MileageInput from './MileageInput';
import { formatNumberWithCommas } from '../../../../../../../utils/CurrencyUtils';
import { formatDate } from '../../../../../../../utils/DateUtils';

interface SubmitMileageDriversTableProps {
  mileageReportRequests: IOdometerHistory[];
  mileageReportRequestsCount: number;
  control: any;
  register?: UseFormRegister<{ assets: MileageReportRequestUpdate[] }>;
  errors?: FieldErrors<{ assets: MileageReportRequestUpdate[] }>;
  offset: number;
  limit: number;
  perPage: number;
  handlePerPageChange: (value: number) => void;
  handlePageChange: (page: number, pageSize: number) => void;
  editingRows: Set<string>;
  onToggleEditing: (truckId: string) => void;
  /** THT-678: Catena-connected customers can't submit mileage manually */
  catenaConnected?: boolean;
}

const TABLE_HEADERS = [
  'Type',
  'Unit Number',
  'Make',
  'Model',
  'Year',
  'VIN Number',
  'Previous Odometer',
  'Current Odometer',
  'Status',
  'Submitted At',
];

export const SubmitMileageDriversTable: React.FC<
  SubmitMileageDriversTableProps
> = ({
  mileageReportRequests,
  offset,
  limit,
  mileageReportRequestsCount,
  perPage,
  control,
  register,
  errors,
  handlePerPageChange,
  handlePageChange,
  editingRows,
  onToggleEditing,
  catenaConnected = false,
}) => {
  // Check if a request has been submitted
  const isSubmitted = (request: IOdometerHistory): boolean => {
    return (
      request.status === 'submitted' ||
      (request.cumulative_odometer != null && request.miles != null)
    );
  };

  // Check if submitted by owner/administrator
  const isSubmittedByOwner = (request: IOdometerHistory): boolean => {
    return request.submitted_by_type === 'primary_owner';
  };

  const renderPagination = () => (
    <div
      className={`flex w-full items-center justify-between gap-4 rounded-b-[8px] border-[1px] border-bordergray bg-white px-4 py-3 sm:flex-col md:flex-row`}
    >
      <div className="flex flex-row items-center space-x-2 sm:min-h-[48px] sm:w-full sm:justify-between md:w-[50%]">
        {mileageReportRequestsCount > 0 && (
          <p>
            <span className="text-graytertiary">Showing</span> {offset + 1} -{' '}
            {Math.min(offset + limit, mileageReportRequestsCount)}{' '}
            <span className="text-graytertiary">of</span>{' '}
            {mileageReportRequestsCount}{' '}
          </p>
        )}
        <Select
          value={perPage}
          onChange={handlePerPageChange}
          className={`w-[40%]`}
        >
          <Select.Option value={20}>20 per page</Select.Option>
          <Select.Option value={50}>50 per page</Select.Option>
          <Select.Option value={100}>100 per page</Select.Option>
        </Select>
      </div>

      <Pagination
        current={Math.floor(offset / limit) + 1}
        pageSize={perPage}
        total={mileageReportRequestsCount}
        onChange={handlePageChange}
        showSizeChanger={false}
      />
    </div>
  );

  return (
    <div className="flex h-full w-full flex-col justify-between overflow-hidden rounded-[8px] border-l-[1px] border-r-[1px] border-t-[1px] border-bordergray bg-white">
      {mileageReportRequests && mileageReportRequests?.length > 0 ? (
        <>
          <div className="w-full overflow-x-auto">
            <table className="min-w-full table-auto rounded-lg bg-white shadow-md">
              <thead className="bg-gray-100">
                <tr>
                  {TABLE_HEADERS.map((header) => (
                    <th
                      key={header}
                      className="px-4 py-2 text-left text-[14px] font-[600] text-primary"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="overflow-y-auto">
                {mileageReportRequests.map((request, index) => {
                  const make = request.truck?.truck_make || '';
                  const model = request.truck?.truck_model || '';
                  const year = request.truck?.truck_year || '';
                  const truckId = request.truck_id || String(index);

                  const submitted = isSubmitted(request);
                  const submittedByOwner = isSubmittedByOwner(request);
                  const isEditing = editingRows.has(truckId);

                  // Get previous odometer value
                  const previousOdometer =
                    request.previous_odometer ??
                    request.truck?.current_mileage ??
                    null;

                  // Row styling: gray out if submitted by owner and not editing
                  const rowClass =
                    submitted && submittedByOwner && !isEditing
                      ? 'border-b border-gray-200 bg-gray-50 text-gray-400'
                      : 'border-b border-gray-200 hover:bg-ctaactivegreen';

                  return (
                    <React.Fragment key={truckId}>
                      <tr className={rowClass}>
                        {/* Type (truck icon) */}
                        <td className="px-4 py-2 text-[14px] text-primary">
                          <img
                            alt="truck-svg"
                            src="/truck.svg"
                            className={`h-[24px] w-[24px] ${submitted && submittedByOwner && !isEditing ? 'opacity-50' : ''}`}
                          />
                        </td>

                        {/* Unit Number */}
                        <td
                          className={`px-4 py-2 text-[14px] ${submitted && submittedByOwner && !isEditing ? 'text-gray-400' : 'text-primary'}`}
                        >
                          {request?.truck?.truck_display_name}
                        </td>

                        {/* Make */}
                        <td
                          className={`px-4 py-2 text-[14px] ${submitted && submittedByOwner && !isEditing ? 'text-gray-400' : 'text-primary'}`}
                        >
                          {make ? make[0].toUpperCase() + make.slice(1) : ''}
                        </td>

                        {/* Model */}
                        <td
                          className={`px-4 py-2 text-[14px] ${submitted && submittedByOwner && !isEditing ? 'text-gray-400' : 'text-primary'}`}
                        >
                          {model ? model[0].toUpperCase() + model.slice(1) : ''}
                        </td>

                        {/* Year */}
                        <td
                          className={`px-4 py-2 text-[14px] ${submitted && submittedByOwner && !isEditing ? 'text-gray-400' : 'text-primary'}`}
                        >
                          {year}
                        </td>

                        {/* VIN Number */}
                        <td
                          className={`px-4 py-2 text-[14px] ${submitted && submittedByOwner && !isEditing ? 'text-gray-400' : 'text-primary'}`}
                        >
                          {request.truck?.vehicle_id_number}
                        </td>

                        {/* Previous Odometer */}
                        <td
                          className={`px-4 py-2 text-[14px] ${submitted && submittedByOwner && !isEditing ? 'text-gray-400' : 'text-primary'}`}
                        >
                          {previousOdometer != null
                            ? formatNumberWithCommas(previousOdometer)
                            : '--'}
                        </td>

                        {/* Current Odometer Input */}
                        <td className="w-[200px] px-4 py-2 text-[14px] text-primary">
                          {/* Hidden fields for PATCH payload */}
                          <input
                            type="hidden"
                            value={request.truck_id ?? ''}
                            {...register?.(`assets.${index}.truck_id`)}
                          />
                          <input
                            type="hidden"
                            value={request?.snapshot_id ?? ''}
                            {...register?.(`assets.${index}.snapshot_id`)}
                          />
                          {request?.created_at?.length && (
                            <MileageInput
                              name={`assets.${index}.cumulative_odometer`}
                              control={control}
                              index={index}
                              errors={errors}
                              defaultValue={request?.cumulative_odometer ?? ''}
                              previousOdometer={previousOdometer}
                              disabled={
                                catenaConnected || (submitted && !isEditing)
                              }
                            />
                          )}
                        </td>

                        {/* Status */}
                        <td className="px-4 py-2 text-[14px]">
                          <div className="flex items-center gap-2">
                            {submitted ? (
                              <>
                                {isEditing ? (
                                  <>
                                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-700">
                                      <PencilSimple size={12} weight="bold" />
                                      Editing
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => onToggleEditing(truckId)}
                                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                                      title="Cancel editing"
                                    >
                                      <X size={14} />
                                      Cancel
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <span
                                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
                                        submittedByOwner
                                          ? 'bg-purple-100 text-purple-700'
                                          : 'bg-green-100 text-green-700'
                                      }`}
                                    >
                                      <CheckCircle size={12} weight="fill" />
                                      {submittedByOwner
                                        ? 'By Owner'
                                        : 'Submitted'}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => onToggleEditing(truckId)}
                                      disabled={catenaConnected}
                                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                                      title="Edit mileage"
                                    >
                                      <PencilSimple size={14} />
                                      Edit
                                    </button>
                                  </>
                                )}
                              </>
                            ) : request?.created_at ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700">
                                <Clock size={12} weight="fill" />
                                Pending
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">--</span>
                            )}
                          </div>
                        </td>

                        {/* Submitted At */}
                        <td
                          className={`px-4 py-2 text-[14px] ${submitted && submittedByOwner && !isEditing ? 'text-gray-400' : 'text-primary'}`}
                        >
                          {request.manually_completed_at
                            ? formatDate(
                                new Date(request.manually_completed_at),
                                true,
                                true
                              )
                            : '--'}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {renderPagination()}
        </>
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center space-y-2">
          <div className="flex h-[64px] w-[64px] items-center justify-center rounded-[4px] border-[1px] border-bordergray bg-[#F5F6F7]">
            <Speedometer size={40} className="text-graysecondary" />
          </div>
          <p className="text-[14px] leading-[20px] text-graysecondary">
            No mileage report requests found.
          </p>
        </div>
      )}
    </div>
  );
};
